import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { mapItems, propertyMappings } from "../utils/utils";
import { postWhislistApi } from "./api";
import { trackChatEvent } from "../utils/analytics";
import pushNotificationManager from '../utils/pushNotifications';

const API_DOMAIN = process.env.REACT_APP_DOMAIN || "https://admin.aitomotivelab.com";
const WS_DOMAIN = process.env.REACT_APP_WEBSOCKET_URL || "wss://admin.aitomotivelab.com";

// Defensive stub for external scripts (GTM, etc.) that may call this function
if (typeof window !== 'undefined' && typeof window.showWhatsAppCta !== 'function') {
  window.showWhatsAppCta = () => {
    console.warn('showWhatsAppCta called but not implemented - this may be from an outdated GTM tag');
  };
}

// WebSocket paths
const WS_PATH = "/ws/ai_response/";
const WEBSOCKET_URL = `${WS_DOMAIN}${WS_PATH}`;

// API paths
const TOKEN_PATH = "/user_management/oauth/token/";
const SET_COOKIES_PATH = "/ai_app/set_cookies/";

const TOKEN_URL = `${API_DOMAIN}${TOKEN_PATH}`;
const SET_COOKIES_URL = `${API_DOMAIN}${SET_COOKIES_PATH}`;

const api = axios.create();

let areCookiesSet = false;
let client_id = null;
let client_secret = null;
let grant_type = null;
let api_token = null;
let form_registration_id = null;
let accessToken = null;
let codice_concessionario = null;
let primary_color = null;
let secondary_color = null;
let background_color = null;
let text_color = null;
let logo = null;
let name_chat = null;

const FORM_TRACKING_DEBOUNCE = 500; // 500ms debounce

// Add this function right after the imports and before useChatWebSocket
const hashString = (str) => {
  // Get content-based components
  const contentParts = str.split('|').map(part => part.trim());
  const urlPart = contentParts[0]?.replace(/[^a-z0-9]/gi, '').slice(-8) || '';
  const titlePart = contentParts[2]?.replace(/[^a-z0-9]/gi, '').slice(0, 8) || '';
  
  // Generate hash
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  // Convert components to base36
  const timestamp = Date.now().toString(36);
  const hashPart = Math.abs(hash).toString(36);
  const randomPart = Math.random().toString(36).substr(2, 4);
  
  // Combine all parts with content
  return `${timestamp}_${urlPart}_${titlePart}_${hashPart}${randomPart}`.toLowerCase();
};

// Utility function to parse reply messages from backend format
const parseReplyMessage = (text) => {
  if (!text || typeof text !== 'string') {
    return { text, replyTo: null };
  }
  
  // Check if message contains reply pattern: "user replied to: ...\n\nrisposta: ..."
  const replyPattern = /user replied to:\s*(.+?)\s*\n\nrisposta:\s*(.+)/is;
  const match = text.match(replyPattern);
  
  if (match) {
    const originalMessage = match[1].trim();
    const replyText = match[2].trim();
    
    return {
      text: replyText, // Only show the reply text in the message
      replyTo: {
        text: originalMessage,
        sender: 'bot' // Assume it's a bot message (most common case)
      }
    };
  }
  
  return { text, replyTo: null };
};

// Helper to validate chat_id - returns null if invalid
const getValidChatId = (value) => {
  if (!value || value === "undefined" || value === "null") return null;
  return value;
};

const useChatWebSocket = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedWishlist, setLoadedWishlist] = useState(false);
  const [colors, setColors] = useState(null);
  const [connectionError, setConnectionError] = useState(false);

  // Simplified chat_id state - use numeric id from backend
  const [chatId, setChatId] = useState(() => 
    getValidChatId(localStorage.getItem("aitomotivelab_personalData_chat_id"))
  );
  const chatIdRef = useRef(chatId);

  useEffect(() => {
    chatIdRef.current = chatId;
    if (chatId && !getValidChatId(localStorage.getItem("aitomotivelab_personalData_chat_id"))) {
      localStorage.setItem("aitomotivelab_personalData_chat_id", chatId);
    }
  }, [chatId]);

  const [wishlistItems, setWishlistItems] = useState([]);
  const [error, setError] = useState(null);
  const lastUserMessage = useRef("");
  const websocketRef = useRef(null);
  const initializeWebSocketRef = useRef(null);
  const [isHumanControlled, setIsHumanControlled] = useState(false);
  const [humanControlMessage, setHumanControlMessage] = useState("");
  const [mandatoryForm, setMandatoryForm] = useState(false);
  const [automaticHumanControl, setAutomaticHumanControl] = useState(false);
  const [controller, setController] = useState(null);
  const [statusReceived, setStatusReceived] = useState(false);
  
  // Timeout ref for stuck loading messages - removes loading message if no response after 45 seconds
  const loadingTimeoutRef = useRef(null);
  const currentLoadingMessageIdRef = useRef(null);

  // Track initial form state
  useEffect(() => {
    
    // Track initial automaticHumanControl state
    if (typeof window !== 'undefined' && window.trackAutomaticHumanControl) {
      window.trackAutomaticHumanControl(automaticHumanControl, {
        source: 'websocket_initial_state',
        isHumanControlled,
        mandatoryForm,
        note: 'Initial state of automaticHumanControl on component mount'
      });
    }
    
    // FOR TESTING: Add function to window to clear stored state
    window.clearFormState = () => {
      localStorage.removeItem("isSubmitted");
      localStorage.removeItem("aitomotivelab_personalData_chat_id");
      // Form state cleared silently
    };
    
    // FOR TESTING: Add function to force new chat creation
    window.forceNewChat = () => {
      localStorage.removeItem("aitomotivelab_personalData_chat_id");
      setChatId(null);
      chatIdRef.current = null;
      // Forcing new chat creation
      // Refresh the page to restart the WebSocket connection
      window.location.reload();
    };
    
    // FOR TESTING: Add function to check automaticHumanControl tracking status
    window.checkAutomaticHumanControlTracking = () => {
      if (typeof window !== 'undefined' && window.getAutomaticHumanControlStatus) {
        return window.getAutomaticHumanControlStatus();
      } else {
        // automaticHumanControl tracking not available
        return null;
      }
    };
  }, []);

  const [isReconnecting, setIsReconnecting] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_INTERVAL = 5000; // 5 seconds

  const [reconnectCountdown, setReconnectCountdown] = useState(0);

  const hasTrackedFormShown = useRef(false);
  const formDisplayCount = useRef(0);
  const lastTrackingTimestamp = useRef(0);
  const hasTrackedRef = useRef(false);

  const [isChatVisible, setIsChatVisible] = useState(false);

  // Add new state for notification
  const [notification, setNotification] = useState(null);

  // Add new state/refs for navigation tracking
  const navigationStatsInterval = useRef(null);
  const lastPageUrl = useRef(window.location.href);

  // Initialize ref from localStorage or current time
  const lastEventTimestampRef = useRef(
    parseInt(localStorage.getItem('last_page_timestamp')) || Date.now()
  );

  // Add new state for active trigger
  const [activeTrigger, setActiveTrigger] = useState(null);

  // Add new ref to store current page's stats_id
  const currentPageStatsId = useRef(null);

  // Add a ref to track if a message was loading when chat was closed
  const wasLoadingWhenClosed = useRef(false);

  // Add new state to track if chat was opened by a trigger (on mobile)
  const [openedByTrigger, setOpenedByTrigger] = useState(false);

  // Add a new ref to track if the chat has been auto-opened on the current page
  const hasTriggeredChatOpenRef = useRef(false);

  // Fix 1: Move carousel tracking inside hook with cleanup
  const loggedCarousels = useRef(new Set());

  // Add these new states for better message handling
  const [streamingMessageId, setStreamingMessageId] = useState(null);

  // Initialize from localStorage and cleanup on unmount
  useEffect(() => {
    const storedCount = localStorage.getItem('form_display_count');
    if (storedCount) {
      formDisplayCount.current = parseInt(storedCount, 10);
    }
    
    return () => {
      hasTrackedFormShown.current = false;
    };
  }, []);

  // Fix 2: Add cleanup for blob URLs
  useEffect(() => {
    return () => {
      // Cleanup blob URLs when component unmounts
      messages.forEach(msg => {
        if (msg.file_url && typeof msg.file_url === 'string' && msg.file_url.startsWith('blob:')) {
          URL.revokeObjectURL(msg.file_url);
        }
      });
      
      // Clear carousel tracking
      loggedCarousels.current.clear();
    };
  }, [messages]);

  const trackFormShown = useCallback(() => {
    const now = Date.now();
    // Prevent tracking if less than debounce time has passed
    if (now - lastTrackingTimestamp.current < FORM_TRACKING_DEBOUNCE) {
      return;
    }

    if (!hasTrackedFormShown.current) {
      hasTrackedFormShown.current = true;
      lastTrackingTimestamp.current = now;
      formDisplayCount.current += 1;
      localStorage.setItem('form_display_count', formDisplayCount.current.toString());
      
      trackChatEvent.formInteraction('personal_data', 'form_shown', {
        display_count: formDisplayCount.current,
        timestamp: new Date().toISOString()
      });
    }
  }, []);

  const initializeApi = async () => {
    if (areCookiesSet) return;

    try {
      const response = await api.post(SET_COOKIES_URL, {
        fullUrl: window.location.href // Pass full URL in the body
      });
      
      // Response data received
      
      client_id = response.data.client_id;
      client_secret = response.data.client_secret;
      grant_type = response.data.grant_type;
      api_token = response.data.api_token;
      form_registration_id = response.data.form_registration_id;
      primary_color = response.data.primary_color;
      secondary_color = response.data.secondary_color;
      background_color = response.data.background_color;
      text_color = response.data.text_color;
      logo = response.data.logo;
      name_chat = response.data.name;
      codice_concessionario = response.data.codice_concessionario;

      //set colors using setColors state
      setColors({
        primary: response.data.primary_color,
        textPrimary: response.data.text_primary_color,
        secondary: response.data.secondary_color,
        textSecondary: response.data.text_secondary_color,
      });

      areCookiesSet = true;
      localStorage.setItem("api_token", api_token);
      localStorage.setItem("primary_color", primary_color);
      localStorage.setItem("secondary_color", secondary_color);
      localStorage.setItem("background_color", background_color);
      localStorage.setItem("text_color", text_color);
      localStorage.setItem("logo", logo);
      localStorage.setItem("name_chat", name_chat);
      localStorage.setItem("codice_concessionario", codice_concessionario);
      localStorage.setItem("company_name", response.data.company_name);
      localStorage.setItem("welcome_message", response.data.welcome_message);
      localStorage.setItem("message_1", response.data.message_1);
      localStorage.setItem("message_2", response.data.message_2);
      localStorage.setItem("message_3", response.data.message_3);
      localStorage.setItem("numero_whatsapp", response.data.numero_whatsapp || "");
      
      // Store push notification config even if undefined for debugging
      localStorage.setItem("vapid_public_key", response.data.vapid_public_key || "undefined");
      localStorage.setItem("service_worker_url", response.data.service_worker_url || "undefined");
      localStorage.setItem("push_subscription_url", response.data.push_subscription_url || "undefined");
      localStorage.setItem("push_status_url", response.data.push_status_url || "undefined");

      // Initialize push notification manager with config from backend
      if (response.data.vapid_public_key && response.data.vapid_public_key !== 'undefined') {
        pushNotificationManager.init({
          vapid_public_key: response.data.vapid_public_key,
          service_worker_url: response.data.service_worker_url,
          push_subscription_url: response.data.push_subscription_url,
          push_status_url: response.data.push_status_url,
        });
      }
    } catch (error) {
      console.error("Error initializing API:", error);
      
      // Log more details about the error
      if (error.response) {
        console.error("Response status:", error.response.status);
        console.error("Response data:", error.response.data);
        console.error("Response headers:", error.response.headers);
      } else if (error.request) {
        console.error("Request was made but no response received:", error.request);
      } else {
        console.error("Error setting up request:", error.message);
      }
      
      setError("Failed to initialize API. Please refresh the page.");
    }
  };

  const getAccessToken = async () => {
    if (accessToken) {
      return accessToken;
    }

    try {
      const response = await api.post(
        TOKEN_URL,
        {
          client_id: client_id,
          client_secret: client_secret,
          grant_type: grant_type,
        },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );
      accessToken = response.data.access_token;
      
      // Store access token in localStorage so api.js functions can use it
      localStorage.setItem("access_token", accessToken);
      
      return accessToken;
    } catch (error) {
      console.error("Error obtaining access token:", error);
      throw new Error("Failed to obtain access token");
    }
  };

  // Define attemptReconnect first
  const attemptReconnect = useCallback(() => {
    if (isReconnecting) return;

    setIsReconnecting(true);
    reconnectAttempts.current += 1;

    if (reconnectAttempts.current <= MAX_RECONNECT_ATTEMPTS) {
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current - 1), 10000); // Cap at 10 seconds
      // Attempting to reconnect...
      setReconnectCountdown(Math.floor(delay / 1000));

      reconnectTimeoutRef.current = setTimeout(() => {
        if (initializeWebSocketRef.current) {
          initializeWebSocketRef.current();
        }
      }, delay);
    } else {
      // Max reconnection attempts reached
      setError(
        "Failed to reconnect after multiple attempts. Please refresh the page."
      );
      setIsReconnecting(false);
    }
  }, [isReconnecting]);

  // Then define initializeWebSocket
  const initializeWebSocket = useCallback(async () => {
    // Prevent multiple simultaneous initialization attempts
    if (websocketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      websocketRef.current.close();
    }

    try {
      // Only initialize API if not already initialized
      if (!areCookiesSet) {
        await initializeApi();
      }
      const token = await getAccessToken();

      const ws = new WebSocket(WEBSOCKET_URL);
      websocketRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsReconnecting(false);
        setConnectionError(false); // Reset connection error state
        reconnectAttempts.current = 0;
        setError(null);
        
        // Send authentication message only after connection is established
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                type: "authenticate",
                token: token,
                fullUrl: window.location.href,
                language: navigator.language || navigator.userLanguage || '',
              })
            );
          }
        }, 100);
      };

      ws.onmessage = handleWebSocketMessage;

      ws.onclose = (event) => {
        setIsConnected(false);
        setIsAuthenticated(false);
        
        // Don't attempt to reconnect if it was a normal closure
        if (event.code !== 1000) {
          setConnectionError(true);
          setError("WebSocket disconnected. Attempting to reconnect...");
          attemptReconnect();
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setIsConnected(false);
        setConnectionError(true);
        setError("WebSocket connection error. Attempting to reconnect...");
        
        // Trigger reconnection on error
        if (ws.readyState !== WebSocket.OPEN) {
          attemptReconnect();
        }
      };
    } catch (error) {
      console.error("Error initializing WebSocket:", error);
      setError(
        error.message || "Failed to initialize chat. Attempting to reconnect..."
      );
      setConnectionError(true);
      attemptReconnect();
    }
  }, [attemptReconnect]);

  // Assign initializeWebSocket to the ref
  initializeWebSocketRef.current = initializeWebSocket;

  useEffect(() => {
    initializeWebSocket();
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [initializeWebSocket]);

  useEffect(() => {
    let countdownInterval;
    if (reconnectCountdown > 0) {
      countdownInterval = setInterval(() => {
        setReconnectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [reconnectCountdown]);

  const handleManualReconnect = () => {
    reconnectAttempts.current = 0;
    setError(null);
    if (initializeWebSocketRef.current) {
      initializeWebSocketRef.current();
    }
  };

  // Fix 3: Safe JSON parsing with error handling
  const handleWebSocketMessage = useCallback((event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch (error) {
      console.error("Failed to parse WebSocket message:", error, event.data);
      return;
    }
    
    // ENHANCED WEBSOCKET LOGGING FOR DEBUGGING NAVIGATION ISSUES
    const currentUrl = window.location.href;
    const isFirstLoad = !getValidChatId(localStorage.getItem('aitomotivelab_personalData_chat_id'));
    
    // Processing WebSocket message of type: data.type
    
    // Mark message as delivered IMMEDIATELY when any complete message arrives
    // This represents the message being RECEIVED by the client (not shown)
    if (data.type === "message_complete" || data.type === "admin_message") {
      // No delay needed - message is received as soon as WebSocket processes it
      markMessageAsDelivered();
    }
    
    if (data.type === "authentication") {
      setIsAuthenticated(data.success);
      if (!data.success) {
        setError("Authentication failed. Please refresh the page.");
      } else {
        // Simplified: use helper to validate chat_id
        const storedChatId = getValidChatId(chatIdRef.current) || 
                            getValidChatId(localStorage.getItem("aitomotivelab_personalData_chat_id"));
        
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          if (storedChatId) {
            // Join existing chat with numeric id
            websocketRef.current.send(JSON.stringify({ type: "join_chat", chat_id: storedChatId }));
          } else {
            // Create new chat - backend returns numeric id
            websocketRef.current.send(JSON.stringify({ type: "create_chat" }));
          }
        }
      }
    } else if (
      data.type === "stream_message" ||
      data.type === "stream_token" ||
      data.type === "message_complete" ||
      data.type === "admin_message" ||
      data.type === "predicted_questions"
    ) {
      // Dispatch event for notification badge only for complete messages
      if (data.type === "message_complete" || data.type === "admin_message") {
        const messageEvent = new MessageEvent('message', {
          data: JSON.stringify({
            type: data.type,
            message: data.message
          })
        });
        window.dispatchEvent(messageEvent);
        
        // Auto-open chat when a complete message arrives and chat is closed
        // Reset loading state and open chat AFTER message is handled
        if (!isChatVisible) {
          setIsLoading(false);
        }
      }
      
      handleIncomingMessage(data);
      
      // Open chat AFTER message is added to prevent welcome screen flash
      if ((data.type === "message_complete" || data.type === "admin_message") && !isChatVisible) {
        setIsChatVisible(true);
      }
    } else if (data.type === "chat_created" || data.type === "chat_joined") {
      // Chat joined/created
      
      if (data.chat_id) {
        setChatId(data.chat_id);
        
        // Only set values if explicitly provided by backend
        if (data.human_control !== undefined) {
          setIsHumanControlled(data.human_control);
          // Backend set human_control
        }
        
        if (data.mandatory_form !== undefined) {
          setMandatoryForm(data.mandatory_form);
          // Backend set mandatory_form
        }
        
        if (data.automatic_human_control !== undefined) {
          setAutomaticHumanControl(data.automatic_human_control);
          // Backend set automatic_human_control
          
          // Track this change with the enhanced logging system
          if (typeof window !== 'undefined' && window.trackAutomaticHumanControl) {
            window.trackAutomaticHumanControl(data.automatic_human_control, {
              source: 'websocket_chat_created_joined',
              messageType: data.type,
              chatId: data.chat_id,
              humanControl: data.human_control,
              mandatoryForm: data.mandatory_form,
              url: window.location.href,
              isFirstLoad,
              timestamp: new Date().toISOString()
            });
          }
        }

        // Set controller info if provided (e.g. when joining an already-controlled chat)
        if (data.controller !== undefined) {
          setController(data.controller);
        }
        
        chatIdRef.current = data.chat_id;
        localStorage.setItem("aitomotivelab_personalData_chat_id", data.chat_id);
        
        setStatusReceived(true); // Mark that we've received the initial status
        
        fetchChatHistory();
      }
    } else if (data.type === "chat_history") {
      // Received chat history
      
      // Parse and format the chat history
      if (data.chat_history) {
        try {
          const parsedHistory = JSON.parse(data.chat_history);
          const formattedMessages = parsedHistory.flatMap((item) =>
            formatChatHistoryItem(item.fields, item.pk)
          );
          
          // Setting messages from chat history
          setMessages(formattedMessages);
        } catch (error) {
          console.error('Error parsing chat history:', error);
          setError("Failed to load chat history.");
        }
      }
    } else if (data.type === "error" && data.message === "Chat not found") {
      setChatId(null);
      chatIdRef.current = null;
      if (event.target && event.target.readyState === WebSocket.OPEN) {
        event.target.send(
          JSON.stringify({
            type: "create_chat",
          })
        );
      } else {
        setError("WebSocket is not connected. Please try again.");
      }
    } else if (data.type === "show_notification") {
      // Received show_notification
      // Clear any existing notification first
      setNotification(null);
      
      // Set new notification with a small delay to ensure animation triggers
      setTimeout(() => {
        setNotification({
          message: data.message,
          duration: data.duration || 10000
        });
      }, 100);
    } else if (data.type === "navigation_trigger") {
      // Received navigation_trigger
      
      // Store trigger ID from the top level of the message
      setActiveTrigger(data.trigger_id);
      
      const trigger = data.trigger_data;
      // Processing trigger type
      switch (trigger.type.toUpperCase()) {
        case 'OPEN_CHAT_WITH_CUSTOM_MESSAGE':
          // Reset loading state to prevent broken UI
          setIsLoading(false);
          
          // Show the proactive message from the agent
          if (trigger.message || trigger.custom_message) {
            // Add message FIRST to prevent welcome screen flash
            // Use setMessages directly to ensure synchronous update before opening chat
            const proactiveMessage = {
              type: 'message_complete',
              message: trigger.custom_message || trigger.message,
              sender: 'server',
              id: `trigger-${data.trigger_id || Date.now()}`,
              isProactive: true
            };
            
            setMessages((prevMessages) => [
              ...prevMessages,
              {
                id: proactiveMessage.id,
                text: proactiveMessage.message,
                sender: 'server',
                status: 'complete',
                isProactive: true
              }
            ]);
          }
          
          // Open chat AFTER message is added (React batches these updates)
          setIsChatVisible(true);
          break;

        case 'SHOW_NOTIFICATION_WITH_CUSTOM_MESSAGE':
          // Processing SHOW_NOTIFICATION_WITH_CUSTOM_MESSAGE trigger
          // Clear any existing notification first
          setNotification(null);
          setIsLoading(false);
          // Set new notification with a small delay to ensure animation triggers
          setTimeout(() => {
            const visibleMessage = trigger.message;
            const messageToSend = trigger.custom_message || trigger.message;
            const notificationData = {
              // Backend semantics:
              // - trigger.message = notification_message (what user sees)
              // - trigger.custom_message = user_message (what we send on click)
              visible_message: visibleMessage,
              message_to_send: messageToSend,
              custom_message: trigger.custom_message,
              message: trigger.message,
              duration: trigger.duration || 10000,
              type: trigger.type.toLowerCase(),
              cta_label: trigger.cta_label,
            };
            // Setting notification data
            setNotification(notificationData);
          }, 100);
          break;

        case 'OPEN_CHAT':
          // Just handle the message without opening chat
          if (trigger.message) {
            handleIncomingMessage({
              type: 'message_complete',
              message: trigger.message,
              sender: 'server'
            });
          }
          break;
      }
    } else if (data.type === "human_control_update") {
      // Received human_control_update
      
      setIsHumanControlled(data.human_control);
      setHumanControlMessage(data.message || "An operator is now available.");
      
      // Update controller information
      if (data.controller !== undefined) {
        setController(data.controller);
        // Updated controller
      }
      
      // Update automatic human control if provided
      if (data.automatic_human_control !== undefined) {
        setAutomaticHumanControl(data.automatic_human_control);
        // Updated automaticHumanControl
        
        // Track this change with the enhanced logging system
        if (typeof window !== 'undefined' && window.trackAutomaticHumanControl) {
          window.trackAutomaticHumanControl(data.automatic_human_control, {
            source: 'websocket_human_control_update',
            messageType: data.type,
            humanControl: data.human_control,
            mandatoryForm: data.mandatory_form,
            controller: data.controller,
            message: data.message,
            url: window.location.href,
            timestamp: new Date().toISOString()
          });
        }
      }
      
      // FIXED: Only update mandatory form if explicitly provided by backend
      // Don't make assumptions based on other fields
      if (data.mandatory_form !== undefined) {
        setMandatoryForm(data.mandatory_form);
        // Backend set mandatoryForm
      } else {
        // Backend did not provide mandatory_form value
      }
      
      setStatusReceived(true); // Mark that we've received a status update
    }
  }, [trackFormShown, isLoading, isChatVisible]);

  const fetchChatHistory = () => {
    if (
      websocketRef.current &&
      websocketRef.current.readyState === WebSocket.OPEN
    ) {
      websocketRef.current.send(
        JSON.stringify({
          type: "fetch_history",
        })
      );
    } else {
      setError("Failed to fetch chat history. Please try again.");
    }
  };

  const formatChatHistoryItem = (message, id) => {
    const formattedMessages = [];
    if (
      message.sender_message !== null 
    ) {
      // Parse reply message if present
      const parsed = parseReplyMessage(message.sender_message);
      
      formattedMessages.push({
        sender: "user",
        text: parsed.text,
        id,
        file_url: message.sender_file_url || null,
        replyTo: parsed.replyTo,
      });
    } else if (message.sender_file_url) {
      formattedMessages.push({
        sender: "user",
        text: "File inviato",
        id,
        file_url: message.sender_file_url,
      });
    }

    // First push the bot message if it exists
    if (message.bot_message || (message.tool && typeof message.tool === 'string' && (message.tool.includes("car_stock") || message.tool === "car_configurator"))) {
      let formattedMessage = {
        sender: "server",
        tool: message.tool || null,
        text: message.bot_message || "",
        id: `${id}-message`,
        file_url: message.bot_file_url || null,
        whishlist: message.whishlist,
      };

      if (
        message.sources &&
        message.tool &&
        typeof message.tool === 'string' &&
        (message.tool.includes("car_stock") || message.tool === "car_configurator")
      ) {
        try {
          if (
            Array.isArray(message.sources) &&
            propertyMappings[message.tool]
          ) {
            formattedMessage.carouselItems = mapItems(
              message.sources,
              propertyMappings[message.tool]
            );
          }
        } catch (error) {
          // Error processing carousel items
        }
      }

      formattedMessages.push(formattedMessage);
    }

    // Then push predicted questions as a separate message if they exist
    if (message.predicted_questions && typeof message.predicted_questions === 'string' && message.predicted_questions.trim() !== '') {
      // Parse the JSON string at formatting time
      let parsedQuestions;
      try {
        parsedQuestions = JSON.parse(message.predicted_questions);
      } catch (e) {
        console.error('Failed to parse predicted questions:', e, 'Raw data:', message.predicted_questions);
        parsedQuestions = null;
      }
    
      if (parsedQuestions && typeof parsedQuestions === 'object') {
        const questionsArray = Array.isArray(parsedQuestions) 
          ? parsedQuestions 
          : Object.values(parsedQuestions);
        
        // Only add if we have valid questions
        if (questionsArray.length > 0) {
          formattedMessages.push({
            sender: "server",
            type: "predicted_questions",
            questions: questionsArray,
            id: `${id}-questions`,
          });
        }
      }
    }

    return formattedMessages;
  };

  const getAllVehicleInfosFromMessages = () => {
    const allVehicleInfo = messages?.reduce((r, v) => {
      if (v.carouselItems) {
        return [...r, ...v.carouselItems];
      }
      return r;
    }, []);
    return allVehicleInfo;
  };

  const updateWishlistFromMessages = () => {
    const vehiclesInfo = getAllVehicleInfosFromMessages();
    const messageWithWishlist = messages?.find(({ whishlist }) =>
      Boolean(whishlist)
    );
    const wishlistWithInfo = messageWithWishlist?.whishlist
      .split(", ")
      ?.map((id) => vehiclesInfo.find(({ vehicleid }) => vehicleid === id))
      .filter(item => item && item.available !== false); // Filter out unavailable vehicles
    setWishlistItems(wishlistWithInfo || []);
  };

  useEffect(() => {
    if (messages.length > 0 && !loadedWishlist) {
      updateWishlistFromMessages();
      setLoadedWishlist(true);
    }
  }, [messages, loadedWishlist, updateWishlistFromMessages, setLoadedWishlist]);

  const handleWishlistUpdate = async (vehicleId) => {
    try {
      const messageId = messages.find(({ carouselItems }) =>
        carouselItems?.find(({ vehicleid }) => vehicleId === vehicleid)
      ).id;
      const numericMessageId = parseInt(messageId, 10);
      if (isNaN(numericMessageId)) {
        throw new Error("Invalid message ID");
      }
      
      // Pass the access token to the API function
      const response = await postWhislistApi(numericMessageId, vehicleId, accessToken);
      if (response) {
        if (wishlistItems?.some(({ vehicleid: vId }) => vehicleId === vId)) {
          setWishlistItems((wishlistItems) =>
            wishlistItems?.filter(({ vehicleid: vId }) => vehicleId !== vId)
          );
        } else {
          const vehiclesInfo = getAllVehicleInfosFromMessages();
          const vehicleInfo = vehiclesInfo.find(
            ({ vehicleid }) => vehicleid === vehicleId
          );
          setWishlistItems((wishlistItems) => [...wishlistItems, vehicleInfo]);
        }
      } else {
        throw new Error("Failed to update wishlist");
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
      setError("Failed to update wishlist. Please try again.");
    }
  };

  const handleIncomingMessage = useCallback((data) => {
    // If this is a complete message and the chat was closed while loading,
    // mark it to be reopened
    if ((data.type === "message_complete" || data.type === "admin_message") && !isChatVisible) {
      if (isLoading || wasLoadingWhenClosed.current) {
        setIsChatVisible(true);
        wasLoadingWhenClosed.current = false;
      }
    }
    
    // Note: Delivered status is handled when message is RECEIVED (in handleWebSocketMessage)
    // Read status is handled when message is VISIBLE (via IntersectionObserver below)
    
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];
      
      // Handle different message types with separate functions
      switch(data.type) {
        case "admin_message":
          return handleAdminMessage(newMessages, data);
        
        case "predicted_questions":
          return handlePredictedQuestions(newMessages, data);
        
        case "stream_message":
          return handleStreamMessage(newMessages, data);
        
        case "stream_token":
          return handleStreamToken(newMessages, data);
        
        case "message_complete":
          return handleMessageComplete(newMessages, data);
        
        default:
          // Unknown message type received
          return newMessages;
      }
    });
  }, [isChatVisible, isLoading]);

  // Separate handler functions for cleaner code:
  const handleStreamToken = useCallback((messages, data) => {
    // Handle individual token streaming
    const token = data.token;
    if (!token) return messages;
    
    // Prefer updating the last loading server message regardless of ID
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.loading && (lastMessage.sender === "server" || lastMessage.sender === "bot")) {
      messages[messages.length - 1] = {
        ...lastMessage,
        text: (lastMessage.text || '') + token,
        loading: true,
      };
      return messages;
    }

    // Otherwise, find any loading server message in the list
    const idx = [...messages].reverse().findIndex(m => m.loading && (m.sender === 'server' || m.sender === 'bot'));
    if (idx !== -1) {
      const realIdx = messages.length - 1 - idx;
      messages[realIdx] = {
        ...messages[realIdx],
        text: (messages[realIdx].text || '') + token,
        loading: true,
      };
      return messages;
    }

    // Create new streaming message if none exists
    const newMessage = {
      sender: "server",
      text: token,
      tool: Array.isArray(data.tool) ? data.tool : (data.tool ? [data.tool] : []),
      id: data.id || `stream-${Date.now()}`,
      loading: true,
      timestamp: new Date().toISOString(),
    };
    messages.push(newMessage);
    
    return messages;
  }, []);

  const handleStreamMessage = useCallback((messages, data) => {
    if (data.status === "tool") {
      const toolName = data.message;
      
      // CRITICAL: Tool messages should NEVER create new message objects
      // They should ONLY update existing loading messages
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        
        // Only update if the last message is from server/bot and is loading
        if ((lastMessage.sender === "server" || lastMessage.sender === "bot") && lastMessage.loading) {
          const updatedToolList = Array.isArray(lastMessage.tool)
            ? [...new Set([...lastMessage.tool, toolName])]
            : [toolName];
          
          messages[messages.length - 1] = {
            ...lastMessage,
            tool: updatedToolList,
            loading: true,
            status: data.status,
          };
        }
        // If there's no loading message to update, DO NOT create a new message
        // Tool information will be handled when the actual content arrives
      }
      // If no messages exist, DO NOT create a placeholder
      // The tool will be attached when actual content arrives
      
      console.log('Tool status updated for existing loading message:', toolName);
    } else {
      // Handle actual message content
      // Validate that we have actual message content
      if (!data.message || data.message.trim() === '') {
        // Empty stream message received, skipping
        return messages;
      }
      
      console.log('🔥 FRONTEND: Received stream_message chunk:', data.message);
      
      // Find existing streaming message or create new one
      const lastMessage = messages[messages.length - 1];
      
      console.log('🔥 FRONTEND: Last message check:', {
        hasLastMessage: !!lastMessage,
        isLoading: lastMessage?.loading,
        sender: lastMessage?.sender,
        currentText: lastMessage?.text
      });
      
      if (lastMessage && lastMessage.loading && lastMessage.sender === "server") {
        // Create a completely new object to force React re-render
        const newText = (lastMessage.text || '') + data.message;
        console.log('🔥 FRONTEND: Appending to existing message:', newText);
        const updatedMessage = {
          ...lastMessage,
          text: newText,
          loading: true,
          // Force React to detect the change with unique key
          streamingKey: Date.now() + Math.random(),
          timestamp: new Date().toISOString(),
        };
        // Replace the message with a new object reference
        messages[messages.length - 1] = updatedMessage;
        setStreamingMessageId(lastMessage.id);
      } else {
        // Create new streaming message only if we have content
        console.log('🔥 FRONTEND: Creating new streaming message');
        const newMessage = {
          sender: "server",
          text: data.message,
          tool: [], // Will be populated by tool status updates
          id: data.id || `stream-${Date.now()}`,
          loading: true,
          timestamp: new Date().toISOString(),
        };
        messages.push(newMessage);
        setStreamingMessageId(newMessage.id);
      }
    }
    
    console.log('🔥 FRONTEND: handleStreamMessage returning messages with length:', messages.length);
    return messages;
  }, []);

  const handleMessageComplete = useCallback((messages, data) => {
    setIsLoading(false);
    setStreamingMessageId(null);
    
    // Clear the loading timeout since we received a response
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    currentLoadingMessageIdRef.current = null;
    
    // If a message was loading when chat was closed, reopen the chat
    if (wasLoadingWhenClosed.current) {
      setIsChatVisible(true);
      wasLoadingWhenClosed.current = false;
    }
    
    // CRITICAL: Clean up any orphaned empty messages before adding the new one
    const cleanedMessages = messages.filter(msg => {
      // Keep user messages and messages with actual content
      if (msg.sender === 'user') return true;
      if (msg.text && msg.text.trim() !== '') return true;
      if (msg.carouselItems && msg.carouselItems.length > 0) return true;
      if (msg.id === 'welcome' || msg.type === 'system') return true;
      
      // Remove empty server/bot messages that aren't loading
      if ((msg.sender === 'server' || msg.sender === 'bot') && !msg.loading) {
        console.log('Cleaning up orphaned empty message:', {
          id: msg.id,
          text: msg.text,
          tool: msg.tool,
          loading: msg.loading
        });
        return false;
      }
      
      return true;
    });
    
    // If we cleaned up messages, update the array reference
    if (cleanedMessages.length !== messages.length) {
      messages.length = 0;
      messages.push(...cleanedMessages);
    }
    
    // Validate that we have actual message content or other valid data
    const hasContent = data.message && data.message.trim() !== '';
    const hasSources = data.sources && Array.isArray(data.sources) && data.sources.length > 0;
    const hasTool = data.tool && Array.isArray(data.tool) && data.tool.length > 0;
    
    // Don't create empty messages without content, sources, or tools
    if (!hasContent && !hasSources && !hasTool) {
      // Empty message_complete received, skipping
      return messages;
    }
    
    const currentMessage = {
      sender: "server",
      text: data.message || '',
      loading: false,
      status: undefined,
      tool: data.tool || [],
      id: data.id,
      timestamp: new Date().toISOString(),
    };

    // Handle carousel items with improved error handling
    if (data.sources && Array.isArray(data.sources)) {
      console.log('🚗 FRONTEND: Processing carousel with tool:', data.tool, 'sources length:', data.sources.length);
      try {
        currentMessage.carouselItems = processCarouselItems(data);
        console.log('🚗 FRONTEND: Processed carousel items:', currentMessage.carouselItems?.length || 0);
      } catch (mappingError) {
        console.error('🚗 FRONTEND: Error processing carousel items:', mappingError);
        currentMessage.carouselItems = data.sources; // Fallback to raw data
      }
    }
    
    currentMessage.whishlist = data.whishlist || "";

    // Check if the last message is loading - if so, update it instead of creating new
    const lastMessage = messages[messages.length - 1] || {};
    if (lastMessage.loading && lastMessage.sender === "server") {
      // Update the existing streaming message to completed state
      console.log('🔥 FRONTEND: Updating existing streaming message to complete');
      messages[messages.length - 1] = {
        ...currentMessage,
        // Keep the accumulated text from streaming if it's longer/more complete
        text: lastMessage.text && lastMessage.text.length > currentMessage.text.length 
              ? lastMessage.text 
              : currentMessage.text
      };
    } else {
      // Only add new message if it's different from the last one
      const isDuplicate = lastMessage && 
        lastMessage.text === currentMessage.text && 
        lastMessage.sender === currentMessage.sender &&
        !lastMessage.loading;
      
      if (!isDuplicate) {
        console.log('🔥 FRONTEND: Creating new complete message');
        messages.push(currentMessage);
      }
    }
    
    return messages;
  }, []);

  // Extract carousel processing logic
  const processCarouselItems = useCallback((data) => {
    let carouselItems;
    
    // Log vehicle data to check for promotion_document__url field
    console.log('🚗 Vehicle Data Received:', data.sources);
    
    // Log specific promotion fields for debugging
    if (data.sources && data.sources.length > 0) {
      console.log('🔍 Checking for promotion fields in first vehicle:');
      const firstVehicle = data.sources[0];
      console.log('- promotion:', firstVehicle.promotion);
      console.log('- promotionDetails:', firstVehicle.promotionDetails);
      console.log('- promotion_document__url:', firstVehicle.promotion_document__url);
      
      // Check all vehicles for promotion_document__url
      const vehiclesWithPromoDoc = data.sources.filter(item => item.promotion_document__url);
      if (vehiclesWithPromoDoc.length > 0) {
        console.log('✅ Found vehicles with promotion_document__url:', vehiclesWithPromoDoc.length);
        vehiclesWithPromoDoc.forEach((vehicle, index) => {
          console.log(`Vehicle ${index + 1} promotion doc URL:`, vehicle.promotion_document__url);
        });
      } else {
        console.log('❌ No vehicles with promotion_document__url found');
      }
    }
    
    // Handle different tool formats and car stock search
    const toolName = Array.isArray(data.tool) ? data.tool[0] : data.tool;
    console.log('🚗 FRONTEND: Tool name for carousel:', toolName);
    
    if (data.type === "admin_message" && data.tool === "car_stock") {
      carouselItems = mapItems(data.sources, propertyMappings["car_stock_search"]);
    } else if (toolName && (toolName === "car_stock_search" || toolName.includes("car_stock") || toolName === "car_configurator")) {
      console.log('🚗 FRONTEND: Using car_stock_search mapping');
      carouselItems = mapItems(data.sources, propertyMappings[toolName] || propertyMappings["car_stock_search"]);
    } else {
      console.log('🚗 FRONTEND: Using raw sources as carousel items');
      carouselItems = data.sources;
    }

    // Track carousel with improved logic
    const carouselId = data.sources.map(item => item.vehicleid || item.id).join(',');
    if (carouselId && !loggedCarousels.current.has(carouselId)) {
      const vehicleInfo = data.sources.map(item => ({
        id: item.vehicleid || item.id,
        title: item.description || item.title,
        brand: item.brand,
        usagetype: item.usagetype,
        km: item.km
      }));

      try {
        trackChatEvent.carouselShown(data.sources.length, vehicleInfo);
        loggedCarousels.current.add(carouselId);
      } catch (trackingError) {
        // Error tracking carousel
      }
    }

    return carouselItems;
  }, []);

  // Add these handler functions right after the processCarouselItems function
  const handleAdminMessage = useCallback((messages, data) => {
    // Clear the loading timeout since we received a response
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    currentLoadingMessageIdRef.current = null;
    setIsLoading(false);
    
    // CRITICAL: Clean up any orphaned empty messages first
    const cleanedMessages = messages.filter(msg => {
      // Keep user messages and messages with actual content
      if (msg.sender === 'user') return true;
      if (msg.text && msg.text.trim() !== '') return true;
      if (msg.carouselItems && msg.carouselItems.length > 0) return true;
      if (msg.id === 'welcome' || msg.type === 'system') return true;
      
      // Remove empty server/bot messages that aren't loading
      if ((msg.sender === 'server' || msg.sender === 'bot') && !msg.loading) {
        console.log('Admin message cleanup - removing orphaned empty message:', {
          id: msg.id,
          text: msg.text,
          tool: msg.tool,
          loading: msg.loading
        });
        return false;
      }
      
      return true;
    });
    
    // If we cleaned up messages, update the array reference
    if (cleanedMessages.length !== messages.length) {
      messages.length = 0;
      messages.push(...cleanedMessages);
    }
    
    // Validate that we have actual message content or valid data
    const hasContent = data.message && data.message.trim() !== '';
    const hasSources = data.sources && Array.isArray(data.sources) && data.sources.length > 0;
    const hasTool = data.tool && data.tool.length > 0;
    
    // Don't create empty admin messages
    if (!hasContent && !hasSources && !hasTool) {
      // Empty admin message received, skipping
      return messages;
    }
    
    const currentMessage = {
      sender: "server",
      text: data.message || '',
      loading: false,
      status: undefined,
      tool: data.tool || [],
      id: data.id,
      timestamp: new Date().toISOString(),
    };
    
    // Update this section to properly map carousel items for admin messages
    if (data.sources && Array.isArray(data.sources)) {
      // Log admin message vehicle data to check for promotion_document__url field
      console.log('👨‍💼 Admin Message Vehicle Data:', data.sources);
      
      // Log specific promotion fields for debugging
      if (data.sources.length > 0) {
        console.log('🔍 Checking for promotion fields in admin message vehicles:');
        const firstVehicle = data.sources[0];
        console.log('- promotion:', firstVehicle.promotion);
        console.log('- promotionDetails:', firstVehicle.promotionDetails);
        console.log('- promotion_document__url:', firstVehicle.promotion_document__url);
        
        // Check all vehicles for promotion_document__url
        const vehiclesWithPromoDoc = data.sources.filter(item => item.promotion_document__url);
        if (vehiclesWithPromoDoc.length > 0) {
          console.log('✅ Found admin message vehicles with promotion_document__url:', vehiclesWithPromoDoc.length);
          vehiclesWithPromoDoc.forEach((vehicle, index) => {
            console.log(`Admin vehicle ${index + 1} promotion doc URL:`, vehicle.promotion_document__url);
          });
        } else {
          console.log('❌ No admin message vehicles with promotion_document__url found');
        }
      }
      
      try {
        // Use the car_stock_search mapping for admin messages with that tool
        const mappingKey = data.tool === 'car_stock_search' ? 'car_stock_search' : data.tool;
        if (propertyMappings[mappingKey]) {
          currentMessage.carouselItems = mapItems(
            data.sources,
            propertyMappings[mappingKey]
          );
        } else {
          // Fallback to raw sources if no mapping exists
          currentMessage.carouselItems = data.sources;
        }

        // Track carousel logic
        const carouselId = data.sources.map(item => item.vehicleid || item.id).join(',');
        if (carouselId && !loggedCarousels.current.has(carouselId)) {
          const vehicleInfo = data.sources.map(item => ({
            id: item.vehicleid || item.id,
            title: item.description || item.title,
            brand: item.brand,
            usagetype: item.usagetype,
            km: item.km
          }));

          try {
            trackChatEvent.carouselShown(data.sources.length, vehicleInfo);
            loggedCarousels.current.add(carouselId);
          } catch (trackingError) {
            // Error tracking carousel
          }
        }
      } catch (mappingError) {
        // Error processing admin message carousel items
        currentMessage.carouselItems = data.sources; // Fallback to raw data
      }
    }
    
    currentMessage.whishlist = data.whishlist || "";
    
    // Check for duplicates before adding
    const lastMessage = messages[messages.length - 1] || {};
    const isDuplicate = lastMessage && 
      lastMessage.text === currentMessage.text && 
      lastMessage.sender === currentMessage.sender &&
      lastMessage.id !== currentMessage.id; // Different IDs means not duplicate
    
    if (!isDuplicate) {
      messages.push(currentMessage);
    } else {
      // Skipping duplicate admin message
    }
    
    return messages;
  }, []);

  const handlePredictedQuestions = useCallback((messages, data) => {
    let questions = [];
    
    // Handle different question formats
    if (data.questions) {
      if (typeof data.questions === 'object' && !Array.isArray(data.questions)) {
        // If it's an object, extract the values
        questions = Object.values(data.questions).map(q => q?.toString() || '');
      } else if (Array.isArray(data.questions)) {
        // If it's already an array, just map to strings
        questions = data.questions.map(q => q?.toString() || '');
      }
    }

    const currentMessage = {
      sender: "server",
      type: "predicted_questions",
      questions: questions,
      id: data.id || `pred-${Date.now()}`,
      loading: false
    };
    
    messages.push(currentMessage);
    return messages;
  }, []);

  const handleSend = async (message, file, audio, personaldata, replyInfo = null) => {
    // When user sends a message, reset the openedByTrigger state
    setOpenedByTrigger(false);
    
    setIsLoading(true);
    hasTrackedRef.current = false;
    
    if (!isAuthenticated) {
      setError("Not authenticated. Please try again.");
      return;
    }

    setError(null);
    let localUrl = null;
    // Parse reply message if it contains the pattern (for reloaded messages)
    let finalMessage = message || "Ti ho inviato un messaggio";
    let finalReplyTo = replyInfo || null;
    
    // If no replyInfo but message contains reply pattern, parse it
    if (!replyInfo && message) {
      const parsed = parseReplyMessage(message);
      finalMessage = parsed.text;
      finalReplyTo = parsed.replyTo;
    }
    
    let userMessage = {
      sender: "user",
      text: finalMessage,
      id: `user-${Date.now()}`,
      timestamp: new Date().toISOString(),
      replyTo: finalReplyTo, // Store reply info in message
    };

    // Create blob URL with cleanup
    if (file) {
      localUrl = URL.createObjectURL(file);
      userMessage.file_url = localUrl;
      userMessage.fileName = file.name;
      userMessage.fileType = file.type;
    } else if (audio) {
      localUrl = URL.createObjectURL(audio);
      userMessage.file_url = localUrl;
      userMessage.fileName = "audio_message.wav";
      userMessage.fileType = "audio/wav";
    }

    const loadingMessageId = `loading-${Date.now()}`;
    currentLoadingMessageIdRef.current = loadingMessageId;
    
    setMessages((prevMessages) => [
      ...prevMessages,
      userMessage,
      { 
        sender: "server", 
        text: "", 
        loading: true, 
        id: loadingMessageId,
        timestamp: new Date().toISOString(),
      },
    ]);
    
    lastUserMessage.current = message;
    
    // Clear any existing loading timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
    }
    
    // Set timeout to remove stuck loading message after 45 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      console.warn('[WebSocket] Loading message timeout - removing stuck loading indicator');
      setMessages((prevMessages) => 
        prevMessages.filter(msg => msg.id !== loadingMessageId)
      );
      setIsLoading(false);
      currentLoadingMessageIdRef.current = null;
      // Don't set error - the message might still come through
    }, 45000);

    try {
      // Format message with reply context if replying
      let formattedMessage = message;
      if (replyInfo && replyInfo.text) {
        formattedMessage = `user replied to: ${replyInfo.text}\n\nrisposta: ${message}`;
      }

      const data = {
        type: file ? "file_message" : audio ? "audio_message" : "chat_message",
        input: formattedMessage,
        file: file ? await fileToBase64(file) : null,
        file_name: file ? file.name : null,
        file_type: file ? file.type : null,
        audio: audio ? await fileToBase64(audio) : null,
        personaldata: personaldata,
        sender_url: window.location.href,
        trigger_id: activeTrigger,
        reply_to: replyInfo ? {
          id: replyInfo.id,
          text: replyInfo.text,
          sender: replyInfo.sender
        } : null,
        ...getPersonalDataFromLocalStorage(),
      };

      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify(data));
        
        // Clear activeTrigger after first message is sent to avoid false attributions
        // The backend has received the trigger_id, so we can safely clear it
        if (activeTrigger) {
          setActiveTrigger(null);
        }
      } else {
        setError("WebSocket is not connected. Attempting to reconnect...");
        setIsLoading(false);
        attemptReconnect();
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");
      setIsLoading(false);
    }

    // Clean up blob URL after sending (with delay to ensure it's used)
    if (localUrl) {
      setTimeout(() => {
        URL.revokeObjectURL(localUrl);
      }, 5000);
    }
  };

  // New function to send messages to backend only (for operator context, not visible to client)
  const handleSendOperatorOnly = async (message) => {
    if (!isAuthenticated) {
      console.error("Not authenticated. Cannot send operator-only message.");
      return;
    }

    try {
      const data = {
        type: "operator_context_message", // Special type for operator-only messages
        input: message,
        sender_url: window.location.href,
        trigger_id: activeTrigger,
        ...getPersonalDataFromLocalStorage(),
      };

      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        console.log('WEBSOCKET: Sending operator-only message:', message);
        websocketRef.current.send(JSON.stringify(data));
        
        // Clear activeTrigger after message is sent
        if (activeTrigger) {
          setActiveTrigger(null);
        }
      } else {
        console.error("WebSocket is not connected. Cannot send operator-only message.");
      }
    } catch (error) {
      console.error("Error sending operator-only message:", error);
    }
  };

  const handleResendLastMessage = () => {
    if (lastUserMessage.current) {
      handleSend(lastUserMessage.current);
    }
  };

  // Add refs to track last status updates to prevent spam
  const lastReadUpdateRef = useRef(0);
  const lastDeliveredUpdateRef = useRef(0);

  const markMessageAsDelivered = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    // Debounce delivered updates to prevent spam (only send once per 2 seconds)
    const now = Date.now();
    if (now - lastDeliveredUpdateRef.current < 2000) {
      console.log("📨 Skipping delivered update - too soon since last update");
      return;
    }
    lastDeliveredUpdateRef.current = now;

    console.log("📨 Marking message as DELIVERED (received by client)");

    try {
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({
          type: "message_status_update",
          status: "delivered"
        }));
        console.log("✅ Sent delivered status to backend");
      }
    } catch (error) {
      console.error("Error marking message as delivered:", error);
    }
  }, [isAuthenticated]);

  const markMessageAsRead = useCallback(() => {
    if (!isAuthenticated) {
      return;
    }

    // Debounce read updates to prevent spam (only send once per 5 seconds)
    const now = Date.now();
    if (now - lastReadUpdateRef.current < 5000) {
      console.log("📖 Skipping read update - too soon since last update");
      return;
    }
    lastReadUpdateRef.current = now;

    console.log("📖 Marking message as READ (visible to user)");

    try {
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({
          type: "message_status_update",
          status: "read"
        }));
        console.log("✅ Sent read status to backend");
      }
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  }, [isAuthenticated]);

  const getPersonalDataFromLocalStorage = () => ({
    first_name:
      localStorage.getItem("aitomotivelab_personalData_first_name") || "",
    last_name:
      localStorage.getItem("aitomotivelab_personalData_last_name") || "",
    email: localStorage.getItem("aitomotivelab_personalData_email") || "",
    gdpr_consent:
      localStorage.getItem("aitomotivelab_personalData_gdpr") === "gdpr",
    marketing_consent:
      localStorage.getItem("aitomotivelab_personalData_marketing") ===
      "marketing",
    phone: localStorage.getItem("aitomotivelab_personalData_phone") || "",
    api_token: localStorage.getItem("api_token") || "",
    language: navigator.language || navigator.userLanguage || "",
  });

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Reset tracking for new conversations
  const resetFormTracking = useCallback(() => {
    hasTrackedFormShown.current = false;
    hasTrackedRef.current = false;
    formDisplayCount.current = 0;
    lastTrackingTimestamp.current = 0;
    localStorage.removeItem('form_display_count');
  }, []);

  // Update the sendNavigationStats function
  const sendNavigationStats = useCallback(() => {
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      const currentUrl = window.location.href;
      const storedTimestamp = parseInt(localStorage.getItem('last_page_timestamp')) || lastEventTimestampRef.current;
      const timeOnPage = Math.floor((Date.now() - storedTimestamp) / 1000);

      // Calculate scroll percentage
      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      );
      const clientHeight = document.documentElement.clientHeight;
      const scrollTop = window.scrollY;
      const scrollPercentage = Math.round(
        (scrollTop / (scrollHeight - clientHeight)) * 100
      );

      /**
       * Analyzes text content significance based on patterns and structure
       */
      const isSignificantText = (text) => {
        if (!text || typeof text !== 'string') return false;
        const cleaned = text.trim();
        
        // Check for meaningful length and word count
        const words = cleaned.split(/\s+/);
        return cleaned.length >= 3 && 
               cleaned.length <= 200 && 
               words.length >= 2;
      };

      /**
       * Determines element importance based on properties and context
       */
      const isSignificantElement = (element) => {
        // Skip invisible elements
        if (element.offsetParent === null) return false;
        
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || 
            style.visibility === 'hidden' || 
            style.opacity === '0') return false;

        // Skip all elements within chat-root
        const chatRoot = document.getElementById('chat-root');
        if (chatRoot && (element === chatRoot || chatRoot.contains(element))) {
          return false;
        }

        // Rest of the significance checks remain the same
        if (element.onclick || 
            element.getAttribute('role') || 
            element.tagName === 'BUTTON' || 
            element.tagName === 'A' || 
            element.tagName === 'INPUT' || 
            element.tagName === 'SELECT') return true;

        const hasTrackingAttr = Array.from(element.attributes)
          .some(attr => attr.name.startsWith('data-'));
        if (hasTrackingAttr) return true;

        return isSignificantText(element.textContent);
      };

      /**
       * Calculates element visibility score (0-1)
       */
      const getVisibilityScore = (rect, viewportHeight, viewportWidth) => {
        if (rect.width === 0 || rect.height === 0) return 0;

        const verticalVisible = Math.min(rect.bottom, viewportHeight) - 
                              Math.max(rect.top, 0);
        const horizontalVisible = Math.min(rect.right, viewportWidth) - 
                                Math.max(rect.left, 0);
        
        const visibleArea = Math.max(0, verticalVisible) * 
                           Math.max(0, horizontalVisible);
        const totalArea = rect.width * rect.height;
        
        return visibleArea / totalArea;
      };

      /**
       * Gets visible elements with their contextual data
       */
      const getVisibleElements = () => {
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const scrollTop = window.scrollY;
        const visibleContent = [];
        
        // Calculate focus area (center 60% of viewport)
        const focusAreaTop = scrollTop + (viewportHeight * 0.2);
        const focusAreaBottom = scrollTop + (viewportHeight * 0.8);
        
        const walker = document.createTreeWalker(
          document.body,
          NodeFilter.SHOW_ELEMENT,
          {
            acceptNode: (node) => {
              // Skip chat elements
              const chatRoot = document.getElementById('chat-root');
              if (chatRoot && (node === chatRoot || chatRoot.contains(node))) {
                return NodeFilter.FILTER_SKIP;
              }

              // Only process elements that might contain meaningful content
              if (!isSignificantElement(node)) return NodeFilter.FILTER_SKIP;
              
              const rect = node.getBoundingClientRect();
              const elementTop = rect.top + scrollTop;
              const elementBottom = elementTop + rect.height;
              
              // Check viewport visibility
              const isInViewport = (
                elementBottom >= scrollTop &&
                elementTop <= scrollTop + viewportHeight &&
                rect.right >= 0 &&
                rect.left <= viewportWidth &&
                rect.width > 0 &&
                rect.height > 0
              );

              // Check if in focus area
              const isInFocusArea = (
                elementTop <= focusAreaBottom &&
                elementBottom >= focusAreaTop
              );

              if (isInViewport && isInFocusArea) {
                return NodeFilter.FILTER_ACCEPT;
              }
              return NodeFilter.FILTER_SKIP;
            }
          }
        );

        while (walker.nextNode()) {
          const element = walker.currentNode;
          const rect = element.getBoundingClientRect();
          
          const elementCenterY = rect.top + (rect.height / 2);
          const viewportCenterY = viewportHeight / 2;
          const distanceFromCenter = Math.abs(elementCenterY - viewportCenterY);
          const centerScore = 1 - (distanceFromCenter / (viewportHeight / 2));

          const text = element.textContent?.trim();
          if (isSignificantText(text)) {
            const contentType = element.tagName === 'A' || element.onclick ? 'interactive' : 'content';
            
            // Generate a stable hash based on content and attributes
            const hashContent = [
              text,
              element.tagName,
              element.id,
              element.className,
              element.getAttribute('href'),
              element.getAttribute('aria-label'),
              element.getAttribute('title'),
              element.getAttribute('alt'),
              element.getAttribute('data-purpose')
            ].filter(Boolean).join('|');
            
            visibleContent.push({
              text: text.substring(0, 100),
              type: contentType,
              relevance: centerScore,
              stats_id: hashString(hashContent),
              purpose: element.getAttribute('aria-label') || 
                      element.getAttribute('title') || 
                      element.getAttribute('alt') || 
                      element.getAttribute('data-purpose')
            });
          }
        }

        // Sort by relevance and clean up the output
        return visibleContent
          .sort((a, b) => b.relevance - a.relevance)
          .map(item => {
            const { relevance, ...cleanItem } = item;
            return Object.fromEntries(
              Object.entries(cleanItem).filter(([_, v]) => v != null)
            );
          });
      };

      // Only generate new stats_id if URL changed
      if (currentUrl !== lastPageUrl.current || !currentPageStatsId.current) {
        // Generate navigation ID only for new pages
        const navHashContent = [
          currentUrl,
          lastPageUrl.current,
          document.title
        ].join('|');
        currentPageStatsId.current = hashString(navHashContent);
      }

      const pageData = {
        type: 'navigation_stats',
        stats_id: currentPageStatsId.current, // Use stored stats_id
        page: {
          current_url: currentUrl,
          previous_url: lastPageUrl.current,
          title: document.title,
          time_on_page: timeOnPage,
          timestamp: new Date().toISOString(),
          scroll_percentage: scrollPercentage
        },
        viewport: {
          device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          width: window.innerWidth,
          height: window.innerHeight
        },
        visible_content: getVisibleElements().map(item => ({
          text: item.text.substring(0, 100),
          type: item.type,
          purpose: item.purpose
        }))
      };

      // Only send if WebSocket is connected
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        try {
          websocketRef.current.send(JSON.stringify(pageData));
        } catch (error) {
          console.error('Error sending navigation stats:', error);
        }
      } else {
        // WebSocket not connected, unable to send navigation stats
      }

      // Only update timestamp when actually changing pages
      if (currentUrl !== lastPageUrl.current) {
        const newTimestamp = Date.now();
        lastEventTimestampRef.current = newTimestamp;
        localStorage.setItem('last_page_timestamp', newTimestamp.toString());
      }
      
      // Update last page URL after the check
      lastPageUrl.current = currentUrl;
    }
  }, []);

  // Update the navigation tracking effect
  useEffect(() => {
    if (isAuthenticated && isConnected) {
      // Initialize timestamp on first connection if not already set
      if (!localStorage.getItem('last_page_timestamp')) {
        const initialTimestamp = Date.now();
        lastEventTimestampRef.current = initialTimestamp;
        localStorage.setItem('last_page_timestamp', initialTimestamp.toString());
      }

      // Send initial stats
      const sendInitialStats = () => {
        // Small delay to ensure DOM is fully loaded
        setTimeout(sendNavigationStats, 100);
      };

      // Send stats on page load
      sendInitialStats();

      // Set up interval for subsequent stats
      const interval = setInterval(sendNavigationStats, 30000);

      // Clean up
      return () => {
        clearInterval(interval);
      };
    }
  }, [isAuthenticated, isConnected, sendNavigationStats]);

  // Reset trigger flag when page changes
  useEffect(() => {
    const handlePageChange = () => {
      // Reset the trigger flag on page navigation
      hasTriggeredChatOpenRef.current = false;
      setOpenedByTrigger(false);
    };

    window.addEventListener('popstate', handlePageChange);
    window.addEventListener('pushstate', handlePageChange);
    window.addEventListener('replacestate', handlePageChange);
    
    return () => {
      window.removeEventListener('popstate', handlePageChange);
      window.removeEventListener('pushstate', handlePageChange);
      window.removeEventListener('replacestate', handlePageChange);
    };
  }, []);

  // Track if message was loading when chat is closed
  useEffect(() => {
    if (!isChatVisible && isLoading) {
      wasLoadingWhenClosed.current = true;
    }
  }, [isChatVisible, isLoading]);

  // Add ref to track which messages have been marked as read
  const markedAsReadRef = useRef(new Set());

  // Mark visible messages as read when chat becomes visible or when scrolling
  useEffect(() => {
    if (!isChatVisible) return;

    // Create intersection observer to detect when messages are actually VISIBLE (read)
    // This is different from delivered (received) - this tracks when user actually SEES the message
    const observer = new IntersectionObserver(
      (entries) => {
        let hasNewVisibleMessages = false;
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageElement = entry.target;
            const messageId = messageElement.getAttribute('data-message-id');
            
            // Only mark server messages as read, and only once
            if (messageId && !markedAsReadRef.current.has(messageId)) {
              const messageData = messages.find(m => String(m.id) === messageId);
              if (messageData && messageData.sender === "server" && messageId !== 'welcome') {
                markedAsReadRef.current.add(messageId);
                hasNewVisibleMessages = true;
              }
            }
          }
        });
        
        // Only send read status if we have new visible messages
        if (hasNewVisibleMessages) {
          setTimeout(() => {
            // Ensure enough time has passed since last delivered update to show the progression
            const timeSinceLastDelivered = Date.now() - lastDeliveredUpdateRef.current;
            const minDelay = timeSinceLastDelivered < 3000 ? 3000 - timeSinceLastDelivered : 0;
            
            setTimeout(() => {
              markMessageAsRead(); // This sets status to 'read' (blue checkmarks) - user has SEEN the message
            }, minDelay);
          }, 1000); // Initial delay to ensure user has actually read the message
        }
      },
      {
        threshold: 0.5, // Message must be 50% visible
        rootMargin: '0px'
      }
    );

    // Observe all message elements
    const messageElements = document.querySelectorAll('[data-message-id]');
    messageElements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [isChatVisible, messages, markMessageAsRead]);

  return {
    isAuthenticated,
    isConnected,
    statusReceived,
    messages,
    isLoading,
    setIsLoading,
    wishlistItems,
    error,
    isReconnecting,
    reconnectCountdown,
    reconnectAttempts: reconnectAttempts.current,
    MAX_RECONNECT_ATTEMPTS,
    handleManualReconnect,
    handleSend,
    handleSendOperatorOnly,
    handleResendLastMessage,
    handleWishlistUpdate,
    isHumanControlled,
    humanControlMessage,
    mandatoryForm,
    automaticHumanControl,
    controller,
    colors,
    accessToken,
    resetFormTracking,
    isChatVisible,
    toggleChat: () => setIsChatVisible(prev => !prev),
    notification,
    setNotification,
    activeTrigger,
    wasLoadingWhenClosed: wasLoadingWhenClosed.current,
    resetLoadingFlag: () => { wasLoadingWhenClosed.current = false; },
    openedByTrigger,
    setOpenedByTrigger,
    chatId,
    websocketRef
  };
};

export default useChatWebSocket;
